/* ──────────────────────────────────────────────────────────────
   HomePage.jsx
   Dashboard tiles – now driven by quality % and statusFromPercent
   ────────────────────────────────────────────────────────────── */
import React, { useState, useMemo } from 'react';

import hrIcon from '../../assets/HeartRate.png';
import brIcon from '../../assets/RespiratoryRate.png';
import spo2Icon from '../../assets/OxygenLevel.png';
import bpIcon from '../../assets/BloodPressure.png';
import bsIcon from '../../assets/BloodSugur.png';
import cholIcon from '../../assets/Cholesterol.png';

import { FaUserCircle }                   from 'react-icons/fa';
import { useVitals }                      from '../../contexts/VitalProvider';
import DocsunVitalsHelper                 from '../../api/DocsunVitalsHelper';
import { statusFromPercent }              from '../../api/status';

import MetricTile   from './MetricTile';
import MetricDetail from './MetricDetail';

import logoPng from '../../assets/truscan_logo.png';
import './HomePage.scss';

/* helper ------------------------------------------------------- */
const fmt = (val, unit = '') =>
  val === null || val === undefined ? '—' : `${val}${unit}`;

/* alias for brevity */
const pct = DocsunVitalsHelper.convertRating;

/* colour palette for the tiles */
const COLOR = {
  heartRate  : '#35C759',
  respRate   : '#1EADFF',
  spo2       : '#35C759',
  bloodSugar : '#0060FF',
  cholesterol: '#FFC400',
  bp         : '#FF5A5F',
};

/* component ---------------------------------------------------- */
export default function HomePage() {
  const v = useVitals();
  const [selected, setSel] = useState(null);

  /* build tiles every time vitals change */
  const tiles = useMemo(() => {
    const bpTxt = v.systolic && v.diastolic
      ? `${v.systolic}/${v.diastolic}` : null;

    return [
      {
        key   : 'heartRate',
        label : 'Heart Rate',
        icon  : <img src={hrIcon} alt='heart-rate' />,
        value : fmt(v.heartRate, ''),
        unit  : 'bpm',
        color : COLOR.heartRate,
        percent: pct(v.hr_score ?? 5),
      },
      {
        key   : 'respRate',
        label : 'Breathing Rate',
        icon  : <img src={brIcon} alt='resp-rate' />,
        value : fmt(v.respRate, ''),
        unit  : 'rpm',
        color : COLOR.respRate,
        percent: pct(v.br_score ?? 5),
      },
      {
        key   : 'spo2',
        label : 'SpO₂',
        icon  : <img src={spo2Icon} alt='spo2-rate' />,
        value : fmt(v.spo2, ''),
        unit  : '%',
        color : COLOR.spo2,
        percent: pct(v.spo2_score ?? 5),
      },
      {
        key   : 'bp',
        label : 'Blood Pressure',
        icon  : <img src={bpIcon} alt='bp-rate' />,
        value : bpTxt ?? '—',
        unit  : '',
        color : COLOR.bp,
        percent: pct(v.bp_score ?? 5),
      },
      {
        key   : 'bloodSugar',
        label : 'Blood Sugar',
        icon  : <img src={bsIcon} alt='bs-rate' />,
        value : fmt(v.bloodSugar ? Math.round(v.bloodSugar,1) : null, ''),
        unit  : 'mg/dL',
        color : COLOR.bloodSugar,
        percent: pct(v.bs_score ?? 5),
      },
      {
        key   : 'cholesterol',
        label : 'Cholesterol',
        icon  : <img src={cholIcon} alt='chol-rate' />,
        value : fmt(v.cholesterol ? Math.round(v.cholesterol * 38.67) : null, ''),
        unit  : 'mg/dL',
        color : COLOR.cholesterol,
        percent: pct(v.chol_score ?? 5),
      },
    ].map(t => ({                      // attach textual status *once*
      ...t,
      status: statusFromPercent(t.percent),
    }));
  }, [v]);

  /* render ------------------------------------------------------ */
  return (
    <div className="home-page">
      {/* top bar */}
      <header className="top-bar">
        <img src={logoPng} alt="logo" className="logo" />
        <FaUserCircle className="avatar" />
      </header>

      {/* tiles */}
      <section className="focus card">
        <h2>My Focus</h2>
        <div className="grid">
          {tiles.map(t => (
            <MetricTile
              key={t.key}
              value={t.value}
              label={t.label}
              color={t.color}
              icon={t.icon}
              onClick={() => setSel(t)}
            />
          ))}
        </div>
      </section>

      <p className="disclaimer">
        The TRUscan.AI is not a diagnostic tool. Our recommendations are for
        informational purposes only. If you experience any issues, please
        consult your doctor.
      </p>

      {selected && (
        <MetricDetail data={selected} onClose={() => setSel(null)} />
      )}
    </div>
  );
}
